import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { createProject } from '../../services/projects';
import { createPage } from '../../services/pages';
import AppHeader from '../../components/AppHeader';
import Icon from '../../components/AppIcon';
import config from '../../config';

import TemplateCard from './components/TemplateCard';
import FilterToolbar from './components/FilterToolbar';
import PreviewModal from './components/PreviewModal';
import BlankPageOption from './components/BlankPageOption';
import EmptyState from './components/EmptyState';

const TemplateSelection = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);


  const templates = [
  {
    id: 1,
    name: "Элегантный зал для мероприятий",
    category: "Залы",
    categoryId: "halls",
    style: "modern",
    description: "Современный шаблон для презентации конференц-залов и банкетных помещений с интегрированным виджетом бронирования",
    thumbnail: "https://images.unsplash.com/photo-1461782296610-c552d61b149a",
    thumbnailAlt: "Просторный конференц-зал с белыми стенами, деревянным полом, рядами черных стульев и большими окнами с естественным освещением",
    screenshots: [
    "https://images.pexels.com/photos/1395964/pexels-photo-1395964.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница шаблона с большим изображением зала и кнопкой бронирования",
    "Секция с галереей фотографий различных залов и их характеристиками",
    "Форма бронирования с календарем и выбором времени проведения мероприятия"],

    features: [
    "Виджет бронирования Аренда Вместе",
    "Галерея фотографий зала",
    "Прайс-лист услуг",
    "Форма обратной связи",
    "Отзывы клиентов",
    "Адаптивный дизайн"],

    isFeatured: true,
    hasBookingWidget: true
  },
  {
    id: 2,
    name: "Профессиональный офис",
    category: "Офисы",
    categoryId: "offices",
    style: "classic",
    description: "Классический шаблон для аренды офисных помещений с детальным описанием характеристик и удобств",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1342fbb-1766936728417.png",
    thumbnailAlt: "Современный офис открытой планировки с белыми столами, черными офисными креслами, компьютерами и большими окнами",
    screenshots: [
    "https://images.pexels.com/photos/1957477/pexels-photo-1957477.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/380768/pexels-photo-380768.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница с панорамным видом офисного пространства и описанием преимуществ",
    "Детальная информация о площади офиса, количестве рабочих мест и инфраструктуре",
    "Раздел с ценами на аренду и дополнительными услугами"],

    features: [
    "Виджет бронирования кабинетов",
    "3D-тур по офису",
    "Калькулятор стоимости",
    "Список удобств",
    "Контактная информация",
    "Карта расположения"],

    isFeatured: true,
    hasBookingWidget: true
  },
  {
    id: 3,
    name: "Креативный коворкинг",
    category: "Коворкинги",
    categoryId: "coworking",
    style: "creative",
    description: "Яркий и современный шаблон для коворкинг-пространств с акцентом на сообщество и удобства",
    thumbnail: "https://images.unsplash.com/photo-1597227271690-d0e300a67446",
    thumbnailAlt: "Коворкинг-пространство с деревянными столами, зелеными растениями, современными стульями и яркими акцентами в интерьере",
    screenshots: [
    "https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница коворкинга с изображением рабочих зон и описанием концепции",
    "Секция с тарифными планами и доступными опциями членства",
    "Галерея с фотографиями переговорных комнат и зон отдыха"],

    features: [
    "Виджет бронирования мест",
    "Тарифные планы",
    "Календарь мероприятий",
    "Виртуальный тур",
    "Отзывы резидентов",
    "Онлайн-оплата"],

    isFeatured: false,
    hasBookingWidget: true
  },
  {
    id: 4,
    name: "Индивидуальное рабочее место",
    category: "Рабочие места",
    categoryId: "workstations",
    style: "minimal",
    description: "Минималистичный шаблон для аренды отдельных рабочих мест с простой навигацией и быстрым бронированием",
    thumbnail: "https://images.unsplash.com/photo-1580152213601-87df3d2c56e6",
    thumbnailAlt: "Индивидуальное рабочее место с белым столом, черным креслом, ноутбуком, настольной лампой и минималистичным декором",
    screenshots: [
    "https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница с фотографией рабочего места и кнопкой быстрого бронирования",
    "Список доступных рабочих мест с фотографиями и характеристиками",
    "Форма бронирования с выбором даты и времени аренды"],

    features: [
    "Список доступных мест",
    "Быстрое бронирование",
    "Почасовая аренда",
    "Описание удобств",
    "Фото рабочих мест",
    "Мобильная версия"],

    isFeatured: true,
    hasBookingWidget: true
  },
  {
    id: 5,
    name: "Премиум банкетный зал",
    category: "Залы",
    categoryId: "halls",
    style: "classic",
    description: "Роскошный шаблон для презентации банкетных залов с акцентом на элегантность и изысканность",
    thumbnail: "https://images.unsplash.com/photo-1494682205630-350bca9ae68b",
    thumbnailAlt: "Роскошный банкетный зал с круглыми столами, белыми скатертями, хрустальными люстрами и элегантным декором",
    screenshots: [
    "https://images.pexels.com/photos/2306281/pexels-photo-2306281.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/1395967/pexels-photo-1395967.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/2306277/pexels-photo-2306277.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница с изображением роскошно оформленного зала для торжеств",
    "Галерея с примерами оформления зала для различных мероприятий",
    "Раздел с пакетами услуг и дополнительными опциями"],

    features: [
    "Виджет бронирования дат",
    "Пакеты услуг",
    "Портфолио мероприятий",
    "Меню и кейтеринг",
    "Виртуальный тур",
    "Калькулятор стоимости"],

    isFeatured: false,
    hasBookingWidget: true
  },
  {
    id: 6,
    name: "Современный бизнес-центр",
    category: "Офисы",
    categoryId: "offices",
    style: "modern",
    description: "Современный шаблон для презентации офисов в бизнес-центрах с подробной информацией об инфраструктуре",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1923dab8e-1766850837118.png",
    thumbnailAlt: "Современный офис с панорамными окнами, белыми столами, эргономичными креслами и минималистичным дизайном",
    screenshots: [
    "https://images.pexels.com/photos/380768/pexels-photo-380768.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/1181622/pexels-photo-1181622.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница с видом на современный офис и описанием бизнес-центра",
    "Детальная информация о доступных офисах с планировками и характеристиками",
    "Раздел с инфраструктурой бизнес-центра и дополнительными услугами"],

    features: [
    "Планировки офисов",
    "Инфраструктура здания",
    "Виртуальный тур",
    "Онлайн-бронирование",
    "Калькулятор аренды",
    "Контакты и локация"],

    isFeatured: false,
    hasBookingWidget: true
  },
  {
    id: 7,
    name: "Технологичный коворкинг",
    category: "Коворкинги",
    categoryId: "coworking",
    style: "modern",
    description: "Технологичный шаблон для IT-коворкингов с акцентом на инновации и современные решения",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1382af0a9-1764641718312.png",
    thumbnailAlt: "Технологичный коворкинг с современными рабочими станциями, большими мониторами, эргономичной мебелью и высокотехнологичным оборудованием",
    screenshots: [
    "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница IT-коворкинга с современным дизайном и технологичными решениями",
    "Секция с описанием технического оснащения и высокоскоростного интернета",
    "Раздел с тарифами и специальными предложениями для стартапов"],

    features: [
    "Высокоскоростной интернет",
    "Переговорные с техникой",
    "Зоны для нетворкинга",
    "Гибкие тарифы",
    "Мероприятия для резидентов",
    "Мобильное приложение"],

    isFeatured: true,
    hasBookingWidget: true
  },
  {
    id: 8,
    name: "Гибкое рабочее пространство",
    category: "Рабочие места",
    categoryId: "workstations",
    style: "modern",
    description: "Гибкий шаблон для аренды рабочих мест с различными опциями и удобной системой бронирования",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_100a29a43-1768384099551.png",
    thumbnailAlt: "Гибкое рабочее пространство с различными типами рабочих мест, от индивидуальных столов до зон для совместной работы",
    screenshots: [
    "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1200",
    "https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=1200"],

    screenshotAlts: [
    "Главная страница с различными вариантами рабочих мест и гибкими условиями аренды",
    "Интерактивная карта с доступными рабочими местами и их характеристиками",
    "Система онлайн-бронирования с выбором даты, времени и типа рабочего места"],

    features: [
    "Интерактивная карта мест",
    "Гибкие условия аренды",
    "Онлайн-оплата",
    "Система лояльности",
    "Мобильное бронирование",
    "Уведомления о доступности"],

    isFeatured: false,
    hasBookingWidget: true
  }];


  const filteredTemplates = templates?.filter((template) => {
    const categoryMatch = selectedCategory === 'all' || template?.categoryId === selectedCategory;
    const styleMatch = selectedStyle === 'all' || template?.style === selectedStyle;
    const featuredMatch = !showFeaturedOnly || template?.isFeatured;

    return categoryMatch && styleMatch && featuredMatch;
  });

  const handleTemplateSelect = async (template) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      // Create project in Firestore
      const project = await createProject(user.uid, {
        name: template.name,
        template: template.id,
        thumbnail: template.thumbnail
      });
      // Create default page
      await createPage(project.id, {
        name: 'Главная',
        slug: 'index',
        sections: [],
        order: 0
      });
      navigate(`/projects/${project.id}/editor`);
    } catch (error) {
      console.error('Error creating project:', error);
      setIsCreating(false);
    }
  };

  const handleBlankPageSelect = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      // Create blank project
      const project = await createProject(user.uid, {
        name: 'Новый сайт',
        template: 'blank'
      });
      // Create default page
      await createPage(project.id, {
        name: 'Главная',
        slug: 'index',
        sections: [],
        order: 0
      });
      navigate(`/projects/${project.id}/editor`);
    } catch (error) {
      console.error('Error creating project:', error);
      setIsCreating(false);
    }
  };

  const handleResetFilters = () => {
    setSelectedCategory('all');
    setSelectedStyle('all');
    setShowFeaturedOnly(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Выбор шаблона - {config.appName}</title>
        <meta name="description" content="Выберите готовый шаблон для вашего сайта аренды помещений или начните с пустой страницы" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <AppHeader
          user={{ name: user?.displayName || 'Пользователь', email: user?.email }}
          onLogout={handleLogout}
        />

        <main className="pt-16 pb-8 px-4 md:px-6 lg:px-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="mb-8 md:mb-10 lg:mb-12">
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-text-secondary hover:text-foreground transition-colors">

                  <Icon name="ArrowLeft" size={20} />
                </button>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                  Выбор шаблона
                </h1>
              </div>
              <p className="text-sm md:text-base text-text-secondary max-w-3xl">
                Выберите готовый шаблон, соответствующий вашему типу бизнеса, или начните с пустой страницы для полной кастомизации
              </p>
            </div>

            <FilterToolbar
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              selectedStyle={selectedStyle}
              onStyleChange={setSelectedStyle}
              showFeaturedOnly={showFeaturedOnly}
              onFeaturedToggle={() => setShowFeaturedOnly(!showFeaturedOnly)}
              onReset={handleResetFilters} />


            <div className="mb-8 md:mb-10 lg:mb-12">
              <BlankPageOption onSelect={handleBlankPageSelect} />
            </div>

            {filteredTemplates?.length > 0 ?
            <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                    Готовые шаблоны
                  </h2>
                  <span className="caption text-text-secondary">
                    {filteredTemplates?.length} {filteredTemplates?.length === 1 ? 'шаблон' : 'шаблонов'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {filteredTemplates?.map((template) =>
                <TemplateCard
                  key={template?.id}
                  template={template}
                  onSelect={handleTemplateSelect}
                  onPreview={setPreviewTemplate} />

                )}
                </div>
              </> :

            <EmptyState onReset={handleResetFilters} />
            }
          </div>
        </main>

        {previewTemplate &&
        <PreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSelect={handleTemplateSelect} />

        }
      </div>
    </>);

};

export default TemplateSelection;